import { useMemo } from 'react';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from 'recharts';
import type { WindowDays } from '../../../services/analytics-service';

interface Props {
  total: number;
  deltaPct: number | null;
  daily: { date: string; count: number; redeemed: number }[];
  windowDays: WindowDays;
  onWindowChange: (w: WindowDays) => void;
}

export default function BragChart({ total, deltaPct, daily, windowDays, onWindowChange }: Props) {
  const data = useMemo(() => daily.map(d => ({
    ...d,
    label: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  })), [daily]);

  const isUp = deltaPct !== null && deltaPct >= 0;
  // Trend direction is a real good/bad signal — map it to the semantic
  // success/error tokens, not a decorative accent.
  const deltaColor = deltaPct === null ? 'text-body-grey' : isUp ? 'text-success' : 'text-error';

  return (
    <div className="bg-white rounded-[10px] p-[18px]">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-heading text-[8px] text-body-grey mb-1 tracking-wide">STAMPS · LAST {windowDays} DAYS</div>
          <div className="font-grotesk font-bold text-[40px] text-primary leading-none">{total.toLocaleString()}</div>
          <div className={`flex items-center gap-1 mt-2 text-[13px] font-grotesk font-medium ${deltaColor}`}>
            <span>
              {deltaPct === null ? 'No prior data' : `${isUp ? '▲' : '▼'} ${Math.abs(deltaPct)}% vs prior`}
            </span>
          </div>
        </div>
        <div className="flex bg-background rounded-full p-1 gap-1">
          {([7, 30, 90] as WindowDays[]).map(w => (
            <button
              key={w}
              onClick={() => onWindowChange(w)}
              className={`px-3 py-1.5 rounded-full text-xs font-grotesk font-bold transition-colors ${
                w === windowDays ? 'bg-pixel-cyan text-teal-ink' : 'text-body-grey'
              }`}
            >
              {w}D
            </button>
          ))}
        </div>
      </div>
      {/* Two unlabelled lines are a guessing game — name both series. */}
      <div className="flex items-center gap-4 mb-2">
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-pixel-cyan" />
          <span className="font-heading text-[8px] text-body-grey">STAMPS</span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-coral" />
          <span className="font-heading text-[8px] text-body-grey">REDEEMED</span>
        </span>
      </div>
      <div className="h-48 md:h-60">
        {data.length === 0 || data.every(d => !d.count) ? (
          <div className="h-full flex items-center justify-center text-body-grey font-grotesk text-[13px] text-center px-4">
            No stamps yet — chart unlocks after your first customer
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
              <CartesianGrid stroke="#e7ded0" strokeDasharray="2 4" />
              <XAxis
                dataKey="label"
                stroke="#8a8578"
                fontSize={10}
                tick={{ fontFamily: 'inherit' }}
                minTickGap={20}
              />
              <YAxis stroke="#8a8578" fontSize={10} tick={{ fontFamily: 'inherit' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: '#fff', border: '2px solid #36d9d3', borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: '#111111', fontFamily: 'inherit' }}
              />
              <Line type="monotone" dataKey="count" stroke="#36d9d3" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Stamps" />
              {/* Recharts takes a colour, not a Tailwind class — #f96a55 IS
                  the `coral` token from tailwind.config.js. Keep them in sync. */}
              <Line type="monotone" dataKey="redeemed" stroke="#f96a55" strokeWidth={2} dot={false} activeDot={{ r: 4 }} name="Redeemed" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
